import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  path?: string;
  children?: { id: string; label: string; path: string }[];
}

const menuItems: MenuItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-3-line', path: '/' },
  {
    id: 'campaigns', label: 'Kampanya Yönetimi', icon: 'ri-megaphone-line',
    children: [
      { id: 'campaign-list', label: 'Tüm Kampanyalar', path: '/kampanyalar' },
      { id: 'campaign-add', label: 'Kampanya Ekle', path: '/kampanyalar/ekle' },
    ]
  },
  { id: 'visitors', label: 'Ziyaretçiler', icon: 'ri-user-location-line', path: '/ziyaretciler' },
  { id: 'events', label: 'Event Kayıtları', icon: 'ri-file-list-3-line', path: '/event-kayitlari' },
  { id: 'funnel', label: 'Funnel Analizi', icon: 'ri-filter-3-line', path: '/funnel' },
  { id: 'leads', label: 'Lead Yönetimi', icon: 'ri-contacts-line', path: '/leadler' },
  { id: 'traffic', label: 'Trafik Kaynakları', icon: 'ri-flow-chart', path: '/trafik' },
  { id: 'utm', label: 'UTM Analizi', icon: 'ri-link-m', path: '/utm' },
  { id: 'reports', label: 'Raporlar', icon: 'ri-bar-chart-box-line', path: '/raporlar' },
  { id: 'realtime', label: 'Canlı Aktivite', icon: 'ri-live-line', path: '/canli' },
];

const bottomItems: MenuItem[] = [
  { id: 'settings', label: 'Ayarlar', icon: 'ri-settings-4-line', path: '/ayarlar' },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<string[]>(['campaigns']);

  const isActive = (path?: string) => {
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const toggleExpand = (id: string) => {
    setExpandedItems(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const renderItem = (item: MenuItem, depth = 0) => {
    const active = isActive(item.path) || (item.children?.some(c => isActive(c.path)) ?? false);
    const expanded = expandedItems.includes(item.id);
    const hasChildren = item.children && item.children.length > 0;

    return (
      <div key={item.id}>
        <button
          onClick={() => {
            if (hasChildren) {
              toggleExpand(item.id);
            } else if (item.path) {
              handleNavigate(item.path);
            }
          }}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap group relative
            ${active
              ? 'bg-champagne-50 text-champagne-600 border-l-[3px] border-champagne-500'
              : 'text-charcoal-400 hover:bg-blush-50 hover:text-charcoal-600'
            }
            ${collapsed ? 'justify-center' : ''}
          `}
          title={collapsed ? item.label : undefined}
        >
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className={`${item.icon} text-[18px] ${active ? 'text-champagne-600' : 'text-charcoal-400 group-hover:text-charcoal-600'}`} />
          </div>
          {!collapsed && (
            <>
              <span className={`text-[14px] font-medium flex-1 text-left ${active ? 'text-champagne-700' : ''}`}>
                {item.label}
              </span>
              {hasChildren && (
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
                </div>
              )}
            </>
          )}
          {collapsed && (
            <div className="absolute left-full ml-3 top-1/2 -translate-y-1/2 bg-charcoal-600 text-white text-xs px-2.5 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none shadow-modal z-50 whitespace-nowrap">
              {item.label}
            </div>
          )}
        </button>

        {hasChildren && expanded && !collapsed && (
          <div className="ml-4 mt-0.5 space-y-0.5 border-l border-border pl-3">
            {item.children!.map(child => (
              <button
                key={child.id}
                onClick={() => handleNavigate(child.path)}
                className={`w-full text-left px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 cursor-pointer whitespace-nowrap
                  ${isActive(child.path)
                    ? 'text-champagne-600 bg-champagne-50'
                    : 'text-charcoal-300 hover:text-charcoal-600 hover:bg-blush-50'
                  }
                `}
              >
                {child.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`fixed top-0 left-0 h-full bg-white border-r border-border flex flex-col z-40 transition-all duration-300 ease-in-out
        ${collapsed ? 'w-[72px]' : 'w-[260px]'}
      `}
      style={{ boxShadow: '4px 0 32px rgba(0,0,0,0.04)' }}
    >
      {/* Logo */}
      <div className={`flex items-center h-[72px] px-4 border-b border-border flex-shrink-0 ${collapsed ? 'justify-center' : 'gap-3'}`}>
        <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center">
          <img src="https://public.readdy.ai/ai/img_res/bfe6411f-5005-4a4f-afcd-8d82bcacf62c.png" alt="Logo" className="w-9 h-9 object-contain rounded-lg" />
        </div>
        {!collapsed && (
          <div>
            <p className="text-[13px] font-700 text-charcoal-600 leading-tight font-bold">Salons</p>
            <p className="text-[11px] text-charcoal-300 leading-tight">Yönetim Paneli</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2.5 space-y-1 scrollbar-hide">
        {menuItems.map(item => renderItem(item))}
      </nav>

      {/* Bottom */}
      <div className="px-2.5 pb-4 space-y-1 border-t border-border pt-3">
        {bottomItems.map(item => renderItem(item))}
        <button
          onClick={onToggle}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-charcoal-300 hover:bg-blush-50 hover:text-charcoal-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`${collapsed ? 'ri-arrow-right-double-line' : 'ri-arrow-left-double-line'} text-[18px]`} />
          </div>
          {!collapsed && <span className="text-[13px] font-medium">Daralt</span>}
        </button>
      </div>
    </aside>
  );
}
